import { Injectable } from '@angular/core';
import { Project } from '@project/shared/project.model';
import { Milestone } from '@project/detail/milestones/milestone.model';
import { MILESTONE_TYPE } from '@project/detail/milestones/milestone-type.enum';
import { MILESTONE_STATUS } from '@program/shared/milestone-status.enum';

@Injectable()
export class MilestonePermissionsService {
  canUpdateMilestoneStatus(milestone: Milestone, project: Project): boolean {
    if (this.isMilestoneWithNoProgramTied(milestone)) {
      return true;
    }

    const { isProjectOwner, isProgramOwner, isReadOnly } = this.extractProjectPermissions(project);
    const allowDevelopersToUpdateMilestoneStatuses = milestone?.milestoneConfig?.program?.programMilestonePolicy?.allowDevelopersToUpdateMilestoneStatuses ||
      milestone?.milestoneConfig?.project?.program?.programMilestonePolicy?.allowDevelopersToUpdateMilestoneStatuses;

    const canUpdateStatus = !isReadOnly && ((allowDevelopersToUpdateMilestoneStatuses && isProjectOwner) || isProgramOwner);

    // allowDevelopersToUpdateMilestoneStatuses takes precedence in this workflow.
    // Only if it is false, we check if isEditableByDevelopers is enabled for status updates.
    if (!canUpdateStatus) {
      return false;
    }

    return canUpdateStatus && this.canEditMilestone(milestone, project);
  }

  canEditMilestone(milestone: Milestone, project: Project): boolean {
    const {
      isProjectOwner,
      isProgramOwner,
      isReadOnly,
      isEditableByDevelopers
    } = this.extractProjectPermissions(project, milestone);

    return !isReadOnly && ((isEditableByDevelopers && isProjectOwner) || isProgramOwner);
  }

  shouldDisplayEditNameAndDescription(milestone: Milestone, project: Project, milestoneType: MILESTONE_TYPE): boolean {
    const {
      isProjectOwner,
      isProgramOwner,
      isReadOnly,
      isEditableByDevelopers
    } = this.extractProjectPermissions(project, milestone);
    // For developers, only the project-level "edit name and description" is editable.
    // Currently, project-level milestones do not have the `editableByDevelopers` flag configurable, so they are all considered editable by default.
    const isEditableByDevelopersForNameAndDescription = isProjectOwner && isEditableByDevelopers && milestoneType === MILESTONE_TYPE.PROJECT;

    return !isReadOnly && (isProgramOwner || isEditableByDevelopersForNameAndDescription);
  }

  shouldHighlightMilestone(milestone: Milestone): boolean {
    const { highlightWhileIncomplete } = this.extractProjectPermissions(null, milestone);
    const status = milestone?.status;

    if (!highlightWhileIncomplete || status === MILESTONE_STATUS.COMPLETED) {
      return false;
    }

    return highlightWhileIncomplete && (status === MILESTONE_STATUS.IN_PROGRESS || status === MILESTONE_STATUS.NOT_STARTED);
  }

  private isMilestoneWithNoProgramTied(milestone: Milestone): boolean {
    return !milestone?.milestoneConfig?.program && !milestone?.milestoneConfig?.project?.program;
  }

  private extractProjectPermissions(project?: Project, milestone?: Milestone) {
    const isProjectOwner = project?.projectPermissions?.currentUser.projectOwner;
    const isProgramOwner = project?.projectPermissions?.currentUser.programOwner;
    const isReadOnly = project?.projectPermissions?.project?.modulePermissions?.milestones?.readOnly;
    const isEditableByDevelopers = milestone?.milestoneConfig?.editableByDevelopers;
    const highlightWhileIncomplete = milestone?.milestoneConfig?.highlightWhileIncomplete;

    return { isProjectOwner, isProgramOwner, isReadOnly, isEditableByDevelopers, highlightWhileIncomplete };
  }
}
